import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Query } from 'graphql/generated/graphqlTypes';
import {
  GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useMemo } from 'react';
import { getVisitorStatuses, Statuses } from 'utils/Visitor/visitor';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitors'>> =
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS;
const GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getNfcCards'>> =
  GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS;

const useDashboardUsers = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();

  const {
    loading: visitorsLoading,
    error: visitorsError,
    data: visitorsData
  } = useQuery(GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (visitorsError) {
      handleFetchError(translation.users_error_fetching);
      console.error(visitorsError);
    }
  }, [visitorsError]);

  const {
    loading: cardsLoading,
    error: cardsError,
    data: cardsData
  } = useQuery(GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (cardsError) {
      handleFetchError(translation.access_card_error_fetching);
      console.error(cardsError);
    }
  }, [cardsError]);

  const loadingDashboardUsers = (visitorsLoading && !visitorsData) || (cardsLoading && !cardsData);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which
  // would keep the "refreshing" indicator on until the next success.
  const isRefetchingDashboardUsers =
    !loadingDashboardUsers && ((!!visitorsData && visitorsLoading) || (!!cardsData && cardsLoading));

  useRegisterBackgroundRefetch(isRefetchingDashboardUsers);

  const userCounts = useMemo(() => {
    const visitors = visitorsData?.getVisitors ?? [];
    const cards = cardsData?.getNfcCards ?? [];

    return visitors.reduce(
      (acc, visitor) => {
        switch (getVisitorStatuses(visitor, cards)) {
          case Statuses.isConfigDeviceDisconnected:
            acc.usersConfigDisconnected++;
            break;
          case Statuses.isAccessDeviceDisconnectedAndVisitorHasNoCards:
            // Without the cards nobody can be said to have none, so no user is counted. A loaded but empty list is
            // valid data. The count of users with a disconnected config app does not depend on the cards.
            if (cardsData) acc.usersDisconnectedWithoutCard++;
            break;
        }

        return acc;
      },
      {
        usersConfigDisconnected: 0,
        usersDisconnectedWithoutCard: 0
      }
    );
  }, [visitorsData, cardsData]);

  return {
    ...userCounts,
    loadingDashboardUsers,
    isRefetchingDashboardUsers
  };
};

export default useDashboardUsers;
